import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import { theme } from "../../../../../utils/global-styles/theme";

import BulletIcon from "../../../../shared/BlurredContentContainer/Assets/BulletIcon";
import DownArrow from "../assets/DownArrow";

const SyllabusAccordion = ({
  index,
  title,
  description,
  numbersAndBullets = true,
}) => {
  const [descriptionHeight, setDescriptionHeight] = useState(0);

  useEffect(() => {
    setDescriptionHeight(index === 0 ? "auto" : 0);
  }, [index]);

  const AccordionGrid = styled.div`
      height: max-content;
      display: grid;
      grid-template-rows: 68px 1fr;
      grid-template-columns: 50px 1fr 43px;
      font-family: ${theme.fontFaces.headers};
      border-bottom: 1px solid ${theme.colors.accent};
      cursor: pointer;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        grid-template-rows: auto 1fr;
        grid-template-columns: 30px 1fr 43px;
        padding: 20px 0 20px 14px;
      }
    `,
    NumberContainer = styled.div`
      grid-area: 1 / 1 / span 1 / span 1;
      display: flex;
      align-items: center;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        align-items: flex-start;
      }
    `,
    SectionNumber = styled.h2`
      font-size: 24px;
      font-weight: bold;
      letter-spacing: -0.63px;
      line-height: 30px;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        font-size: 20px;
        font-weight: bold;
        letter-spacing: -0.52px;
        line-height: 30px;
      }
    `,
    SectionTitleContainer = styled.div`
      grid-area: 1 / 2 / span 1 / span 1;
      display: flex;
      align-items: center;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        padding-right: 20px;
      }
    `,
    SectionTitle = styled.h2`
      font-size: 24px;
      font-weight: 500;
      letter-spacing: -0.22px;
      line-height: 30px;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        font-size: 20px;
        font-weight: 500;
        letter-spacing: -0.18px;
        line-height: 24px;
      }
    `,
    ArrowContainer = styled.div`
      grid-area: 1 / 3 / span 1 / span 1;
      display: flex;
      align-items: center;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        align-items: flex-start;
        padding-top: 5px;
      }
    `,
    DescriptionContainer = styled.div`
      grid-area: 2/ 2 / span 1 / span 1;
      overflow-y: hidden;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        padding-right: 20px;
      }
    `,
    DescriptionTextContainer = styled.div`
      display: flex;
      align-items: baseline;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        align-items: flex-start;
        padding-top: 5px;
      }
    `,
    BulletContainer = styled.div`
      height: 100%;
      width: 16px;
    `,
    DescriptionText = styled.p`
      font-family: ${theme.fontFaces.paragraphs};
      font-size: 20px;
      letter-spacing: -0.57px;
      line-height: 31px;
      padding-left: 10px;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        font-size: 15px;
        letter-spacing: -0.43px;
        line-height: 20px;
      }
    `;

  return (
    <>
      <AccordionGrid
        onClick={() =>
          setDescriptionHeight(descriptionHeight === 0 ? "auto" : 0)
        }
      >
        <SectionTitleContainer>
          <SectionTitle>{title}</SectionTitle>
        </SectionTitleContainer>
        <ArrowContainer>
          <DownArrow
            fillColorClassName={"dkBlue-fill"}
            activeDisplay={descriptionHeight === 0 ? false : true}
          />
        </ArrowContainer>
        <DescriptionContainer
          css={
            descriptionHeight === 0
              ? css`
                  max-height: ${descriptionHeight};
                  -webkit-animation: slideUp 1s ease-in-out; /* Safari, Chrome and Opera > 12.1 */
                  -moz-animation: slideUp 1s ease-in-out; /* Firefox < 16 */
                  -ms-animation: slideUp 1s ease-in-out; /* Internet Explorer */
                  -o-animation: slideUp 1s ease-in-out;
                  animation: slideUp 1s ease-in-out;

                  @media only screen and (max-width: ${theme.breakpoints
                      .helper800}) {
                    padding-bottom: 0;
                  }
                `
              : css`
                  max-height: ${descriptionHeight};
                  padding-bottom: 20px;
                  -webkit-animation: slideDown 1s ease-in-out; /* Safari, Chrome and Opera > 12.1 */
                  -moz-animation: slideDown 1s ease-in-out; /* Firefox < 16 */
                  -ms-animation: slideDown 1s ease-in-out; /* Internet Explorer */
                  -o-animation: slideDown 1s ease-in-out;
                  animation: slideDown 1s ease-in-out;

                  @media only screen and (max-width: ${theme.breakpoints
                      .helper800}) {
                    padding-bottom: 0;
                  }
                `
          }
        >
          {description
            .split("- ")
            .slice(1)
            .map((bullet, index) => {
              return (
                <DescriptionTextContainer key={index}>
                  {numbersAndBullets && (
                    <BulletContainer>
                      <BulletIcon height="15px" width="15px" />
                    </BulletContainer>
                  )}
                  <DescriptionText>{bullet}</DescriptionText>
                </DescriptionTextContainer>
              );
            })}
        </DescriptionContainer>
      </AccordionGrid>
    </>
  );
};

SyllabusAccordion.propTypes = {
  index: PropTypes.number,
  title: PropTypes.string,
  description: PropTypes.string,
};

export default SyllabusAccordion;
